/**
 * Clientes empresa, cartera, reporte y configuración de CAUCARED.
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { CAU, est, fmt, fecha, ESTADOS_PAGO, Pastilla, mensajeDeError, logoCau } from './cauComun';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// El dígito de verificación se muestra mientras se escribe (el servidor lo vuelve a calcular)
const PRIMOS = [3, 7, 13, 17, 19, 23, 29, 37, 41, 43, 47, 53, 59, 67, 71];
const dv = (nit) => {
  const d = String(nit || '').replace(/\D/g, '');
  if (!d || d.length > 15) return '';
  let s = 0;
  for (let i = 0; i < d.length; i++) s += Number(d[d.length - 1 - i]) * PRIMOS[i];
  const r = s % 11;
  return String(r > 1 ? 11 - r : r);
};

// ─── CLIENTES ────────────────────────────────────────────────

const CLIENTE_VACIO = {
  razon_social: '', tipo_doc: 'NIT', documento: '', contacto: '', telefono: '', email: '', direccion: '', ciudad: '',
  aplica_retefuente: false, retefuente_pct: 2.5, aplica_reteica: false, reteica_pct: 0, aplica_reteiva: false, notas: '', activo: true,
};

export function CauClientes({ onVerFacturas }) {
  const [lista, setLista] = useState([]);
  const [q, setQ] = useState('');
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try { const { data } = await api.get('/api/caucared/clientes', { params: { q } }); setLista(data); setError(''); }
    catch (e) { setError(mensajeDeError(e, 'No se pudieron cargar los clientes')); }
  }, [q]);
  useEffect(() => { const t = setTimeout(cargar, 250); return () => clearTimeout(t); }, [cargar]);

  const guardar = async () => {
    setError(''); setGuardando(true);
    try {
      if (editando.id) await api.put(`/api/caucared/clientes/${editando.id}`, editando);
      else await api.post('/api/caucared/clientes', editando);
      setEditando(null); cargar();
    } catch (e) { setError(mensajeDeError(e, 'No se pudo guardar el cliente')); }
    finally { setGuardando(false); }
  };

  if (editando) {
    const c = editando;
    const set = (k, v) => setEditando({ ...c, [k]: v });
    const campo = (k, txt, extra = {}) => (
      <div><label style={est.label}>{txt}</label>
        <input style={est.input} value={c[k] ?? ''} onChange={(e) => set(k, e.target.value)} {...extra} /></div>
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button style={est.botonSec} onClick={() => setEditando(null)}>← Volver</button>
          <h2 style={{ margin: 0, fontSize: '18px' }}>{c.id ? c.razon_social : 'Nuevo cliente empresa'}</h2>
        </div>
        <div style={{ ...est.tarjeta, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: '12px' }}>
          {campo('razon_social', 'Razón social o nombre *')}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: '0 0 90px' }}><label style={est.label}>Tipo</label>
              <select style={est.input} value={c.tipo_doc} onChange={(e) => set('tipo_doc', e.target.value)}>
                <option>NIT</option><option>CC</option><option>CE</option><option>PAS</option></select></div>
            <div style={{ flex: 1, minWidth: 0 }}><label style={est.label}>Número {c.tipo_doc === 'NIT' && c.documento ? `· DV ${dv(c.documento)}` : ''}</label>
              <input style={est.input} value={c.documento ?? ''} onChange={(e) => set('documento', e.target.value)} placeholder="Sin dígito de verificación" /></div>
          </div>
          {campo('contacto', 'Persona de contacto')}
          {campo('telefono', 'Teléfono / WhatsApp')}
          {campo('email', 'Correo de facturación', { type: 'email' })}
          {campo('direccion', 'Dirección')}
          {campo('ciudad', 'Ciudad')}
        </div>
        <div style={est.tarjeta}>
          <div style={{ fontWeight: 700, marginBottom: '4px' }}>Retenciones que practica esta empresa</div>
          <div style={{ fontSize: '12px', color: CAU.tenue, marginBottom: '10px' }}>
            Si el cliente es agente retenedor, te descuenta esto al pagar. Pregúntale a su contabilidad o mira un pago anterior.
            Se copian a cada factura nueva y ahí se pueden cambiar.
          </div>
          <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '13px' }}>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="checkbox" checked={!!c.aplica_retefuente} onChange={(e) => set('aplica_retefuente', e.target.checked)} /> En la fuente
              <input type="number" step="0.01" style={{ ...est.input, width: '76px', padding: '5px 7px' }} value={c.retefuente_pct ?? ''} onChange={(e) => set('retefuente_pct', e.target.value)} />%
            </label>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="checkbox" checked={!!c.aplica_reteica} onChange={(e) => set('aplica_reteica', e.target.checked)} /> De ICA
              <input type="number" step="0.001" style={{ ...est.input, width: '86px', padding: '5px 7px' }} value={c.reteica_pct ?? ''} onChange={(e) => set('reteica_pct', e.target.value)} />%
            </label>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input type="checkbox" checked={!!c.aplica_reteiva} onChange={(e) => set('aplica_reteiva', e.target.checked)} /> De IVA (15% del IVA)
            </label>
          </div>
          <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '8px' }}>ICA en porcentaje: 10 por mil = 1 · 6,9 por mil = 0,69.</div>
        </div>
        <div style={est.tarjeta}>
          <label style={est.label}>Notas internas</label>
          <textarea rows={3} style={{ ...est.input, resize: 'vertical' }} value={c.notas ?? ''} onChange={(e) => set('notas', e.target.value)} />
          {c.id && (
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '10px', fontSize: '13px' }}>
              <input type="checkbox" checked={!!c.activo} onChange={(e) => set('activo', e.target.checked)} /> Activo (los inactivos no salen al escoger cliente)
            </label>
          )}
        </div>
        {error && <div style={est.error}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button style={est.botonSec} onClick={() => setEditando(null)}>Cancelar</button>
          <button style={est.boton} disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <input style={{ ...est.input, flex: '1 1 220px', width: 'auto' }} placeholder="Buscar por nombre, NIT o contacto…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button style={est.boton} onClick={() => setEditando({ ...CLIENTE_VACIO })}>+ Nuevo cliente</button>
      </div>
      {error && <div style={{ ...est.error, marginBottom: '12px' }}>{error}</div>}
      {!lista.length && !error && (
        <div style={{ ...est.tarjeta, textAlign: 'center', color: CAU.tenue, padding: '32px' }}>
          {q ? 'Nadie con ese nombre.' : 'Aún no hay clientes de CAUCARED. Son aparte de los clientes de internet de LinkUs.'}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {lista.map((c) => (
          <div key={c.id} style={{ ...est.tarjeta, padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', opacity: c.activo ? 1 : 0.5 }}>
            <div style={{ flex: '1 1 200px', minWidth: 0, cursor: 'pointer' }} onClick={() => setEditando({ ...c })}>
              <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{c.razon_social}{!c.activo && ' (inactivo)'}</div>
              <div style={{ fontSize: '12px', color: CAU.tenue }}>
                {[c.documento && `${c.tipo_doc} ${c.documento}${c.dv != null && c.tipo_doc === 'NIT' ? '-' + c.dv : ''}`, c.contacto, c.telefono, c.ciudad].filter(Boolean).join(' · ')}
              </div>
              <div style={{ fontSize: '11px', color: CAU.tenue }}>
                {[c.aplica_retefuente && `Retefuente ${Number(c.retefuente_pct)}%`, c.aplica_reteica && `ReteICA ${Number(c.reteica_pct)}%`, c.aplica_reteiva && 'ReteIVA'].filter(Boolean).join(' · ')}
              </div>
            </div>
            <div style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => onVerFacturas(c)}>
              <div style={{ fontSize: '12px', color: CAU.tenue }}>{c.facturas} factura{c.facturas === 1 ? '' : 's'}</div>
              {Number(c.saldo) > 0 && <div style={{ fontWeight: 700, color: CAU.acento }}>Debe {fmt(c.saldo)}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── CARTERA ─────────────────────────────────────────────────

export function CauCartera({ onVerFactura }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get('/api/caucared/cartera').then(({ data }) => setD(data)).catch((e) => setError(mensajeDeError(e, 'No se pudo cargar la cartera')));
  }, []);
  if (error) return <div style={est.error}>{error}</div>;
  if (!d) return <div style={{ color: CAU.tenue }}>Cargando…</div>;

  const porCliente = Object.values(d.facturas.reduce((acc, f) => {
    const k = f.cliente_nombre;
    acc[k] = acc[k] || { nombre: k, saldo: 0, n: 0 };
    acc[k].saldo += Number(f.saldo); acc[k].n++;
    return acc;
  }, {})).sort((a, b) => b.saldo - a.saldo);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,180px),1fr))', gap: '12px' }}>
        <Cifra txt="Por cobrar" valor={fmt(d.total)} />
        <Cifra txt="Vencido" valor={fmt(d.vencido)} color={d.vencido > 0 ? CAU.rojo : undefined} />
        <Cifra txt="Facturas abiertas" valor={d.facturas.length} />
      </div>
      <div style={{ fontSize: '12px', color: CAU.tenue }}>Solo facturas de CAUCARED. La cartera de internet de LinkUs está en su propia pantalla y no se mezcla.</div>
      {!d.facturas.length && <div style={{ ...est.tarjeta, textAlign: 'center', color: CAU.tenue, padding: '32px' }}>Nadie le debe a CAUCARED. 🎉</div>}
      {porCliente.length > 1 && (
        <div style={est.tarjeta}>
          <div style={{ fontWeight: 700, marginBottom: '8px' }}>Por cliente</div>
          {porCliente.map((c) => (
            <div key={c.nombre} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '6px 0', borderBottom: `1px solid ${CAU.lineas}`, fontSize: '13px' }}>
              <span style={{ overflowWrap: 'anywhere' }}>{c.nombre} <span style={{ color: CAU.tenue }}>· {c.n}</span></span><b>{fmt(c.saldo)}</b>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {d.facturas.map((f) => (
          <div key={f.id} onClick={() => onVerFactura(f.id)}
            style={{ ...est.tarjeta, padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', cursor: 'pointer' }}>
            <div style={{ minWidth: '92px' }}>
              <div style={{ fontWeight: 800, color: CAU.acento }}>{f.numero}</div>
              <div style={{ fontSize: '12px', color: CAU.tenue }}>vence {fecha(f.fecha_vencimiento)}</div>
            </div>
            <div style={{ flex: '1 1 180px', minWidth: 0, overflowWrap: 'anywhere' }}>{f.cliente_nombre}
              {Number(f.dias_vencida) > 0 && <div style={{ fontSize: '12px', color: CAU.rojo }}>{f.dias_vencida} días vencida</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 800 }}>{fmt(f.saldo)}</div>
              {Number(f.pagado) > 0 && <div style={{ fontSize: '11px', color: CAU.tenue }}>de {fmt(f.neto)}</div>}
            </div>
            <Pastilla info={ESTADOS_PAGO[f.estado_pago]} />
          </div>
        ))}
      </div>
    </div>
  );
}

const Cifra = ({ txt, valor, color, sub }) => (
  <div style={est.tarjeta}>
    <div style={{ fontSize: '11px', color: CAU.tenue, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{txt}</div>
    <div style={{ fontSize: '22px', fontWeight: 800, color: color || '#fff', marginTop: '4px' }}>{valor}</div>
    {sub && <div style={{ fontSize: '12px', color: CAU.tenue }}>{sub}</div>}
  </div>
);

// ─── REPORTE ─────────────────────────────────────────────────

export function CauReporte() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [mesAbierto, setMesAbierto] = useState(null);
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    api.get('/api/caucared/reporte', { params: { anio } }).then(({ data }) => setD(data)).catch((e) => setError(mensajeDeError(e, 'No se pudo cargar el reporte')));
  }, [anio, vuelta]);
  // Al corregir un costo en el desglose, los totales de arriba se vuelven a pedir
  const recargar = () => setVuelta((v) => v + 1);

  const suma = (k) => (d ? d.meses.reduce((s, m) => s + m[k], 0) : 0);
  const sinCosto = suma('renglones_sin_costo');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button style={est.botonSec} onClick={() => { setD(null); setMesAbierto(null); setAnio(anio - 1); }}>←</button>
        <b style={{ fontSize: '18px' }}>{anio}</b>
        <button style={est.botonSec} onClick={() => { setD(null); setMesAbierto(null); setAnio(anio + 1); }}>→</button>
      </div>
      {error && <div style={est.error}>{error}</div>}
      {d && (<>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,170px),1fr))', gap: '12px' }}>
          <Cifra txt="Vendido (sin IVA)" valor={fmt(suma('subtotal'))} sub={`${suma('facturas')} facturas`} />
          <Cifra txt="Ganancia" valor={fmt(suma('ganancia'))} color={CAU.verde}
            sub={suma('subtotal') ? `${Math.round(suma('ganancia') / suma('subtotal') * 100)}% de lo vendido` : ''} />
          <Cifra txt="Facturado (con IVA)" valor={fmt(suma('total'))} sub="Lo que pagan los clientes" />
          <Cifra txt="Cobrado" valor={fmt(suma('cobrado'))}
            sub={suma('total') - suma('retenciones') - suma('cobrado') > 0 ? `Falta ${fmt(suma('total') - suma('retenciones') - suma('cobrado'))}` : 'Todo cobrado'} />
          <Cifra txt="IVA facturado" valor={fmt(suma('iva'))} sub="Es de la DIAN, no ganancia" />
        </div>
        {sinCosto > 0 && (
          <div style={{ ...est.tarjeta, borderColor: CAU.borde, fontSize: '13px' }}>
            ⚠️ {sinCosto} renglón{sinCosto > 1 ? 'es' : ''} facturado{sinCosto > 1 ? 's' : ''} sin costo conocido cuenta{sinCosto > 1 ? 'n' : ''} ganancia cero: la ganancia real es mayor que la que se ve aquí. Toca el mes para ver cuáles son y ponerles el costo.
          </div>
        )}
        <div style={{ ...est.tarjeta, padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
            <thead><tr>
              {['Mes', 'Cotizaciones', 'Facturas', 'Vendido', 'IVA', 'Retenciones', 'Ganancia', 'Cobrado'].map((h, i) => (
                <th key={h} style={{ ...est.th, textAlign: i ? 'right' : 'left' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {d.meses.map((m) => (
                <tr key={m.mes} onClick={() => m.facturas && setMesAbierto(mesAbierto === m.mes ? null : m.mes)}
                  style={{ opacity: m.facturas || m.cotizaciones || m.cobrado ? 1 : 0.35, cursor: m.facturas ? 'pointer' : 'default',
                    background: mesAbierto === m.mes ? CAU.acentoSuave : 'transparent' }}>
                  <td style={est.td}>{MESES[m.mes - 1]}{m.facturas ? <span style={{ color: CAU.acento }}> {mesAbierto === m.mes ? '▾' : '▸'}</span> : null}</td>
                  <td style={{ ...est.td, textAlign: 'right' }}>{m.cotizaciones}{m.cotizaciones_facturadas ? ` (${m.cotizaciones_facturadas} fact.)` : ''}</td>
                  <td style={{ ...est.td, textAlign: 'right' }}>{m.facturas}</td>
                  <td style={{ ...est.td, textAlign: 'right' }}>{fmt(m.subtotal)}</td>
                  <td style={{ ...est.td, textAlign: 'right' }}>{fmt(m.iva)}</td>
                  <td style={{ ...est.td, textAlign: 'right' }}>{fmt(m.retenciones)}</td>
                  <td style={{ ...est.td, textAlign: 'right', color: CAU.verde, fontWeight: 700 }}>{fmt(m.ganancia)}</td>
                  <td style={{ ...est.td, textAlign: 'right' }}>{fmt(m.cobrado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!mesAbierto && d.meses.some((m) => m.facturas) && (
          <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '-6px' }}>Toca un mes para ver de dónde sale su ganancia, factura por factura.</div>
        )}
        {mesAbierto && <Desglose anio={anio} mes={mesAbierto} onCambio={recargar} />}
        {d.clientes.length > 0 && (
          <div style={est.tarjeta}>
            <div style={{ fontWeight: 700, marginBottom: '8px' }}>Mejores clientes del año</div>
            {d.clientes.map((c) => (
              <div key={c.cliente_nombre} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', padding: '6px 0', borderBottom: `1px solid ${CAU.lineas}`, fontSize: '13px' }}>
                <span style={{ overflowWrap: 'anywhere' }}>{c.cliente_nombre} <span style={{ color: CAU.tenue }}>· {c.facturas}</span></span>
                <span>{fmt(c.subtotal)} <span style={{ color: CAU.verde }}>· {fmt(c.ganancia)}</span></span>
              </div>
            ))}
          </div>
        )}
      </>)}
    </div>
  );
}

/**
 * De dónde sale la ganancia de un mes: cada factura, cada renglón, qué costo se usó y de
 * dónde salió. Se hizo porque Víctor miró el total y dijo «la ganancia no da»; un total
 * solo no deja ver cuál renglón está sin costo o con un costo que no es.
 */
function Desglose({ anio, mes, onCambio }) {
  const [facturas, setFacturas] = useState(null);
  const [error, setError] = useState('');
  const cargar = useCallback(() => {
    api.get('/api/caucared/reporte/desglose', { params: { anio, mes } })
      .then(({ data }) => { setFacturas(data); setError(''); })
      .catch((e) => setError(mensajeDeError(e, 'No se pudo cargar el desglose')));
  }, [anio, mes]);
  useEffect(() => { setFacturas(null); cargar(); }, [cargar]);

  const ponerCosto = async (it) => {
    const actual = it.costo_unitario == null ? '' : String(it.costo_unitario);
    const txt = window.prompt('¿Cuánto te costó cada unidad de «' + it.descripcion + '»?\n(Lo que le pagaste al proveedor. Vacío = no lo sé.)', actual);
    if (txt === null) return;
    const limpio = txt.replace(/[^\d]/g, '');
    try {
      await api.put('/api/caucared/items/' + it.id + '/costo', { costo: limpio === '' ? null : Number(limpio) });
      cargar(); onCambio();
    } catch (e) { setError(mensajeDeError(e, 'No se pudo guardar el costo')); }
  };

  if (error) return <div style={est.error}>{error}</div>;
  if (!facturas) return <div style={{ color: CAU.tenue }}>Cargando el desglose…</div>;
  const th = (t, der) => <th style={{ ...est.th, textAlign: der ? 'right' : 'left' }}>{t}</th>;
  const td = (v, extra = {}) => <td style={{ ...est.td, textAlign: 'right', whiteSpace: 'nowrap', ...extra }}>{v}</td>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ fontWeight: 700 }}>De dónde sale la ganancia de {MESES[mes - 1].toLowerCase()} {anio}</div>
      {facturas.map((f) => (
        <div key={f.id} style={{ ...est.tarjeta, padding: 0, overflowX: 'auto' }}>
          <div style={{ padding: '12px 14px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'baseline' }}>
            <b style={{ color: CAU.acento }}>{f.numero}</b>
            <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{f.cliente_nombre} · {fecha(f.fecha)}</span>
            <span>Ganancia <b style={{ color: CAU.verde }}>{fmt(f.ganancia)}</b></span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
            <thead><tr>
              {th('Producto')}{th('Cant.', 1)}{th('Venta', 1)}{th('Sin IVA', 1)}{th('Costo c/u', 1)}{th('De dónde sale el costo')}{th('Ganancia', 1)}<th style={est.th} />
            </tr></thead>
            <tbody>
              {f.items.map((it) => {
                const sinCosto = it.de_catalogo && it.costo_unitario == null;
                return (
                  <tr key={it.id}>
                    <td style={{ ...est.td, overflowWrap: 'anywhere' }}>{it.descripcion}{it.sku ? <div style={{ fontSize: '11px', color: CAU.tenue }}>{it.sku}</div> : null}</td>
                    {td(it.cantidad)}
                    {td(fmt(it.venta))}
                    {td(fmt(it.venta_sin_iva), { color: CAU.tenue })}
                    {td(it.costo_unitario == null ? '—' : fmt(it.costo_unitario))}
                    <td style={{ ...est.td, fontSize: '12px', color: sinCosto ? CAU.ambar : CAU.tenue }}>{it.origen}</td>
                    {td(fmt(it.ganancia), { color: it.ganancia < 0 ? CAU.rojo : CAU.verde, fontWeight: 700 })}
                    <td style={{ ...est.td, textAlign: 'right' }}>
                      <button style={{ ...est.botonSec, padding: '4px 10px', fontSize: '12px', ...(sinCosto ? { borderColor: CAU.ambar, color: CAU.ambar } : {}) }}
                        onClick={() => ponerCosto(it)}>{sinCosto ? 'Poner costo' : 'Corregir'}</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      <div style={{ fontSize: '12px', color: CAU.tenue }}>
        La ganancia se cuenta sin IVA: lo que vendiste sin IVA menos lo que te costó sin IVA (el IVA que pagaste al comprar se descuenta del que cobras).
        Un producto sin costo conocido cuenta cero; un servicio escrito a mano sin costo cuenta entero como ganancia.
      </div>
    </div>
  );
}

// ─── CONFIGURACIÓN ───────────────────────────────────────────

export function CauConfig({ cfg, onGuardado, esSuper }) {
  const [c, setC] = useState(cfg);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setC(cfg); }, [cfg]);

  const set = (k, v) => { setC((p) => ({ ...p, [k]: v })); setOk(''); };
  const setCons = (tipo, k, v) => { setC((p) => ({ ...p, consecutivos: p.consecutivos.map((x) => (x.tipo === tipo ? { ...x, [k]: v } : x)) })); setOk(''); };

  const subirLogo = (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    if (archivo.size > 1.5 * 1024 * 1024) { setError('El logo pesa más de 1,5 MB. Guárdalo más pequeño (unos 600 px de ancho bastan).'); return; }
    const r = new FileReader();
    r.onload = () => { const [cab, datos] = String(r.result).split(','); set('logo_base64', datos); set('logo_mime', cab.replace(/^data:|;base64$/g, '')); };
    r.readAsDataURL(archivo);
  };

  const guardar = async () => {
    setError(''); setGuardando(true);
    try {
      const { data } = await api.put('/api/caucared/config', c);
      onGuardado(data); setOk('Guardado.');
    } catch (e) { setError(mensajeDeError(e, 'No se pudo guardar')); }
    finally { setGuardando(false); }
  };

  const campo = (k, txt, extra = {}) => (
    <div><label style={est.label}>{txt}</label>
      <input style={est.input} value={c[k] ?? ''} onChange={(e) => set(k, e.target.value)} disabled={!esSuper} {...extra} /></div>
  );
  const texto = (k, txt, ayuda) => (
    <div><label style={est.label}>{txt}</label>
      <textarea rows={4} style={{ ...est.input, resize: 'vertical' }} value={c[k] ?? ''} onChange={(e) => set(k, e.target.value)} disabled={!esSuper} />
      {ayuda && <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '3px' }}>{ayuda}</div>}</div>
  );
  const rejilla = { ...est.tarjeta, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,220px),1fr))', gap: '12px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {!esSuper && <div style={{ ...est.tarjeta, fontSize: '13px' }}>Solo el superadministrador puede cambiar estos datos.</div>}
      <div style={{ ...est.tarjeta, display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ width: '160px', height: '80px', background: '#fff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <img alt="Logo" src={logoCau(c)} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
        {esSuper && (
          <div>
            <label style={{ ...est.botonSec, display: 'inline-block' }}>Cambiar logo<input type="file" accept="image/*" style={{ display: 'none' }} onChange={subirLogo} /></label>
            {c.logo_base64 && <button style={{ ...est.botonSec, marginLeft: '8px', color: CAU.rojo }} onClick={() => set('logo_base64', '')}>Volver al de fábrica</button>}
            <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '6px' }}>Sale en el PDF de cotizaciones y facturas. Mejor PNG con fondo transparente.</div>
          </div>
        )}
        <div><label style={est.label}>Color de los documentos</label>
          <input type="color" value={c.color || CAU.acento} disabled={!esSuper} onChange={(e) => set('color', e.target.value)} style={{ width: '60px', height: '36px', border: 'none', background: 'none' }} /></div>
      </div>

      <div style={rejilla}>
        {campo('nombre', 'Nombre o razón social')}
        {campo('nit', 'NIT (con dígito de verificación)', { placeholder: '900.000.000-0' })}
        {campo('direccion', 'Dirección')}
        {campo('ciudad', 'Ciudad')}
        {campo('telefono', 'Teléfono')}
        {campo('email', 'Correo')}
        {campo('website', 'Página web')}
      </div>

      <div style={rejilla}>
        <div><label style={est.label}>¿Cobra IVA?</label>
          <select style={est.input} value={c.responsable_iva === false ? 'no' : 'si'} disabled={!esSuper} onChange={(e) => set('responsable_iva', e.target.value === 'si')}>
            <option value="si">Sí, es responsable de IVA</option><option value="no">No es responsable de IVA</option></select></div>
        {c.responsable_iva !== false && campo('iva_pct', 'IVA por defecto (%)', { type: 'number' })}
        <div><label style={est.label}>Precio sugerido de los productos</label>
          <select style={est.input} value={c.margen_modo || 'tienda'} disabled={!esSuper} onChange={(e) => set('margen_modo', e.target.value)}>
            <option value="tienda">El mismo precio de la tienda</option>
            <option value="escala">Escala de márgenes de LinkUs sobre el costo real</option>
            <option value="fijo">Un % fijo sobre el costo real</option></select>
          <div style={{ fontSize: '11px', color: CAU.tenue, marginTop: '3px' }}>
            {c.margen_modo === 'fijo' ? 'Si el costo real no se conoce, se usa el precio de la tienda y se avisa.'
              : c.margen_modo === 'escala' ? '35% en lo barato bajando hasta 12% en lo caro. Sin costo conocido, precio de la tienda.'
              : 'El mismo precio de la tienda, con el IVA incluido. En cada renglón se puede cambiar.'}
          </div></div>
        {c.margen_modo === 'fijo' && campo('margen_pct', 'Margen sobre el costo (%)', { type: 'number' })}
        {campo('validez_dias', 'Validez de las cotizaciones (días)', { type: 'number' })}
        {campo('plazo_dias', 'Plazo de pago de las facturas (días)', { type: 'number' })}
      </div>

      <div style={rejilla}>
        {(c.consecutivos || []).map((k) => (
          <div key={k.tipo} style={{ display: 'flex', gap: '8px' }}>
            <div style={{ flex: '0 0 90px' }}><label style={est.label}>Prefijo {k.tipo === 'factura' ? 'factura' : 'cotización'}</label>
              <input style={est.input} value={k.prefijo} disabled={!esSuper} onChange={(e) => setCons(k.tipo, 'prefijo', e.target.value)} /></div>
            <div style={{ flex: 1 }}><label style={est.label}>Siguiente número</label>
              <input type="number" min="1" style={est.input} value={k.siguiente} disabled={!esSuper} onChange={(e) => setCons(k.tipo, 'siguiente', e.target.value)} /></div>
          </div>
        ))}
        {campo('titulo_factura', 'Título de la factura en el PDF', { placeholder: 'FACTURA DE VENTA' })}
      </div>
      <div style={{ fontSize: '12px', color: CAU.tenue, marginTop: '-6px' }}>
        Si CAUCARED ya venía numerando sus documentos, pon aquí el número que sigue para no repetir.
      </div>

      <div style={rejilla}>
        {texto('condiciones', 'Condiciones de las cotizaciones', 'Forma de pago, garantía, tiempo de entrega… Se copian a cada cotización nueva.')}
        {texto('leyenda_legal', 'Texto legal (sale en facturas y cotizaciones)', 'Resolución de facturación DIAN, régimen, o «Documento soporte, la factura electrónica se envía por correo».')}
        {texto('pie', 'Pie de página')}
      </div>

      {error && <div style={est.error}>{error}</div>}
      {ok && <div style={{ color: CAU.verde, fontSize: '13px' }}>{ok}</div>}
      {esSuper && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={est.boton} disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar configuración'}</button>
        </div>
      )}
    </div>
  );
}
